import React, { useState, useRef, useEffect } from 'react';
import { IoSettingsOutline } from 'react-icons/io5';

export type ViewMode = 'default' | 'condensed';
export type ScrollMode = 'pagination' | 'infinite';

interface ReviewsSettingsProps {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  scrollMode: ScrollMode;
  onScrollModeChange: (mode: ScrollMode) => void;
}

export const ReviewsSettings: React.FC<ReviewsSettingsProps> = ({
  viewMode,
  onViewModeChange,
  scrollMode,
  onScrollModeChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
        title="View settings"
      >
        <IoSettingsOutline size={20} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
          <div className="p-2">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              View Mode
            </div>
            <button
              onClick={() => {
                onViewModeChange('default');
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm rounded-md ${
                viewMode === 'default'
                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              Default
            </button>
            <button
              onClick={() => {
                onViewModeChange('condensed');
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm rounded-md ${
                viewMode === 'condensed'
                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              Condensed
            </button>

            <div className="mt-4 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Scroll Mode
            </div>
            <button
              onClick={() => {
                onScrollModeChange('pagination');
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm rounded-md ${
                scrollMode === 'pagination'
                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              Pagination
            </button>
            <button
              onClick={() => {
                onScrollModeChange('infinite');
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm rounded-md ${
                scrollMode === 'infinite'
                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              Infinite Scroll
            </button>
          </div>
        </div>
      )}
    </div>
  );
}; 